/**
 * PostHog for the web app. Single shared project across web /
 * mobile / extension / api; events are filterable per surface via
 * the auto-injected `$lib` property plus an explicit `platform` tag.
 *
 * Auto-captures: pageviews, clicks, rage clicks, dead clicks, errors,
 * session replays (rate-limited to identified users).
 */
import posthog from 'posthog-js';

const KEY = 'phc_qJ8NB8HZCVbbZK3ps4HyrDV8pHRErjQ7GvsUYBovbF7G';
const HOST = 'https://us.i.posthog.com';

let started = false;

export function initPostHog() {
  if (started) return;
  started = true;

  posthog.init(KEY, {
    api_host: HOST,
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    capture_exceptions: true,                   // catches uncaught errors
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,              // never record what people type
      // maskTextSelector applies `*`-match masking to these tags, so
      // DM composer + E2E chat bubbles + channel post captions are
      // opaque blocks in replays. Prevents replay leaking private
      // conversations even to analytics staff.
      maskTextSelector: [
        '[data-ph-mask]',               // explicit opt-in
        '.ph-mask',                     // class-based opt-in
        '[data-e2e-chat]',              // secret chat timelines
      ].join(','),
    },
    loaded: (ph) => {
      ph.register({ platform: 'web' });
      // Expose for devtools — `posthog.captureException(...)` etc. from
      // the console. posthog-js doesn't attach itself to window by
      // default under bundler-managed module graphs, so we do it here.
      try { (window as any).posthog = ph; } catch { /* SSR / hardened env */ }
    },
  });
}

/**
 * Call this after the user is authenticated (login / session restore).
 * Links anonymous events to the real user id so funnels stitch.
 */
export function identifyPostHog(user: { id: string; email?: string | null; username?: string | null; display_name?: string | null }) {
  if (!started) return;
  posthog.identify(user.id, {
    email: user.email ?? undefined,
    username: user.username ?? undefined,
    name: user.display_name ?? undefined,
  });
}

export function resetPostHog() {
  if (!started) return;
  posthog.reset();
}

export { posthog };
